import { ButtonGroup } from '@startuptools/ui/ButtonGroup';
import CancelIcon from '@mui/icons-material/Cancel';
import { IconButton } from '@startuptools/ui/IconButton';
import { Input } from '@startuptools/ui/Input';
import SaveIcon from '@mui/icons-material/Save';
import { Select } from '@startuptools/ui/Select';
import { Option } from '@startuptools/ui/Option';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { StakeTableRow } from './Row';
import { type GqlCapTableStake } from '../../../graphql/base-types.graphql';
import { Controller, useForm } from 'react-hook-form';
import { StakeKind } from '@startuptools/common/cap-table';
import { Trans, useLingui } from '@lingui/react';

type Stake = Pick<GqlCapTableStake, 'id' | 'name' | 'kind' | 'stake' | 'acqCost' | 'comment'>;

export const StakeForm = ({
  close,
  stake,
  onSave,
  editableFields,
  renderwiseQuantity,
}: {
  close: () => void;
  stake?: Stake;
  editableFields?: (keyof Stake)[];
  onSave: (stake: Stake) => Promise<void>;
  renderwiseQuantity: boolean;
}) => {
  const { register, handleSubmit, control, formState } = useForm<Stake>({ defaultValues: stake });
  const { i18n } = useLingui();
  const formId = `stake-form-${stake?.id ?? 'new'}`;
  const isEditable = (field: keyof Stake) => !editableFields || editableFields.includes(field);
  return (
    <>
      <form
        id={formId}
        onSubmit={handleSubmit(async data => {
          await onSave(data);
          close();
        })}
      />

      <StakeTableRow
        renderwiseQuantity={renderwiseQuantity}
        slots={{
          toggle: null,
          wiseQuantity: null,

          name: isEditable('name') && (
            <Input {...register('name', { required: true })} color={formState.errors.name ? 'danger' : 'neutral'} />
          ),
          kind: isEditable('kind') && (
            <Controller
              name="kind"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, ...props }, formState }) => (
                <Select
                  size="sm"
                  color={formState.errors.kind ? 'danger' : 'neutral'}
                  placeholder={<Trans id="Kind" />}
                  value={value}
                  onChange={(_e, value) => {
                    return props.onChange(value);
                  }}
                >
                  <Option value={StakeKind.Options}>
                    <Trans id="Options" />
                  </Option>
                  <Option value={StakeKind.Shares}>
                    <Trans id="Shares" />
                  </Option>
                </Select>
              )}
            />
          ),
          stake: isEditable('stake') && (
            <Controller
              control={control}
              name="stake"
              rules={{ required: true, min: 0 }}
              render={({ field: { value, ...field }, formState }) => (
                <NumericFormatInput
                  size="sm"
                  value={value ?? ''}
                  {...field}
                  lang={i18n.locale}
                  color={formState.errors.stake ? 'danger' : 'neutral'}
                />
              )}
            />
          ),
          value: null,
          acqCost: isEditable('acqCost') && (
            <Controller
              control={control}
              name="acqCost"
              rules={{ required: true, min: 0 }}
              render={({ field: { value, ...field }, formState }) => (
                <NumericFormatInput
                  size="sm"
                  value={value ?? ''}
                  {...field}
                  lang={i18n.locale}
                  color={formState.errors.acqCost ? 'danger' : 'neutral'}
                  endDecorator={<Trans id="SEK" />}
                />
              )}
            />
          ),

          profit: null,

          share: null,

          comment: isEditable('comment') && <Input {...register('comment')} size="sm" />,
          actions: (
            <ButtonGroup>
              <IconButton type="submit" color="success" form={formId}>
                <SaveIcon />
              </IconButton>
              <IconButton onClick={close} color="danger">
                <CancelIcon />
              </IconButton>
            </ButtonGroup>
          ),
        }}
        component="td"
      />
    </>
  );
};
